import React, { useEffect, useState, useCallback } from "react";
import { Helmet } from "react-helmet-async";
import {
  Container, Grid, Card, Box, Stack, Typography, Divider, Button,
  Collapse, Alert, TextField, MenuItem,
} from "@mui/material";
import { InfoOutlined } from "@mui/icons-material";
import PageHeader from "../../components/PageHeader/PageHeader";
import ReadinessJourneyTile from "../../components/Readiness/ReadinessJourneyTile";
import ReadinessStatusChip from "../../components/Readiness/ReadinessStatusChip";
import ApiCall from "../../utils/apicall";

/**
 * Readiness — AIC-001-BLD-WEB Phase 3 (web port of the AI Compliance Coach journeys).
 * Reads GET /readiness?jobId=  -> { engineVersion, overall, journeys, statuses[] }.
 * Journey tiles are worst-of roll-ups; the checklist lists each rule with its status,
 * guidance, and an "Explain" that calls the boundary-safe coach (suggestion only).
 */
const JOURNEY_META = {
  S: { title: "Setup", subtitle: "Roles, Job Number, and system readiness" },
  D: { title: "Data Population", subtitle: "Imported heats/pieces, cross-checked" },
  P: { title: "First Project", subtitle: "Fabrication controls: issue, release, QC, load" },
};

export default function Readiness() {
  const [jobId, setJobId] = useState("");
  const [jobs, setJobs] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [openJourney, setOpenJourney] = useState(null);
  const [coach, setCoach] = useState({}); // ruleId -> suggestion

  useEffect(() => {
    ApiCall.get("/jobs").then((r) => {
      const list = r.data?.data?.jobs || r.data?.data || [];
      setJobs(Array.isArray(list) ? list : []);
    }).catch(() => {});
  }, []);

  const fetchReadiness = useCallback((jid) => {
    if (!jid) return;
    setLoading(true);
    ApiCall.get(`/readiness?jobId=${jid}`)
      .then((r) => setData(r.data?.data || r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (jobId) fetchReadiness(jobId); }, [jobId, fetchReadiness]);

  const explain = async (ruleId) => {
    try {
      const r = await ApiCall.get(`/readiness/coach/explain?ruleId=${ruleId}`);
      setCoach((c) => ({ ...c, [ruleId]: r.data?.data || r.data }));
    } catch {
      setCoach((c) => ({ ...c, [ruleId]: { suggestion: "Coach unavailable.", disclaimer: "" } }));
    }
  };

  const journeysPresent = data ? Object.keys(JOURNEY_META).filter((j) => (data.journeys || {})[j]) : [];
  const rowsFor = (j) => (data?.statuses || []).filter((s) => s.journey === j);
  const openCount = (j) => rowsFor(j).filter((s) => s.status !== "GREEN").length;

  return (
    <Container maxWidth="lg" sx={{ pb: 6 }}>
      <Helmet><title>Readiness | SFM</title></Helmet>
      <PageHeader
        breadcrumbs={[{ label: "Readiness" }]}
        title="Readiness"
        guide={{
          why: "Show, in one place, whether this job is clear to fabricate and ship — and exactly what to fix when it isn't.",
          who: "The QM and supervisors clearing a job; anyone who needs to know why a job is RED before work starts.",
          what: "A live GREEN/AMBER/RED readiness check across Setup, Data, and First-Project controls, each traced to an AISC/IMP-001 rule.",
          how: "Pick a job; open a RED tile to see the failing rules and guidance; fix the underlying record and re-check.",
          when: "Before releasing a job to fabrication, and any time a heat, hold, or load changes.",
          standard: "AISC 207-25 · AISC 303 · IMP-001",
        }}
      />

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
          <TextField
            select fullWidth size="small" label="Job" value={jobId}
            onChange={(e) => setJobId(e.target.value)} sx={{ maxWidth: 360 }}
          >
            <MenuItem value="">— Select a job —</MenuItem>
            {jobs.map((j) => (
              <MenuItem key={j.id} value={j.id}>{j.name || j.jobNumber || `Job ${j.id}`}</MenuItem>
            ))}
          </TextField>
          {data && (
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography variant="body2" color="text.secondary">Overall:</Typography>
              <ReadinessStatusChip status={data.overall} size="large" />
              <Typography variant="caption" color="text.secondary">engine {data.engineVersion}</Typography>
            </Stack>
          )}
        </Stack>
        {data && Array.isArray(data._unbound) && data._unbound.length > 0 && (
          <Alert severity="warning" sx={{ mt: 1.5 }}>
            Readiness partial — {data._unbound.length} data source(s) pending schema bind. Statuses shown
            for bound rules only. (AIC-001-BLD-WEB Phase 2 sign-off item.)
          </Alert>
        )}
      </Card>

      {loading && <Typography color="text.secondary">Evaluating…</Typography>}

      {data && (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            {journeysPresent.map((j) => (
              <Grid item xs={12} md={4} key={j}>
                <ReadinessJourneyTile
                  title={JOURNEY_META[j].title}
                  subtitle={JOURNEY_META[j].subtitle}
                  status={data.journeys[j]}
                  openCount={openCount(j)}
                  onOpen={() => setOpenJourney(openJourney === j ? null : j)}
                />
              </Grid>
            ))}
          </Grid>

          {journeysPresent.map((j) => (
            <Collapse in={openJourney === j} key={`c-${j}`} unmountOnExit>
              <Card sx={{ p: 2, mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  {JOURNEY_META[j].title} — checklist
                </Typography>
                <Divider sx={{ mb: 1 }} />
                {rowsFor(j).map((s) => (
                  <Box key={s.ruleId} sx={{ py: 1 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 44 }}>{s.ruleId}</Typography>
                        <ReadinessStatusChip status={s.status} />
                      </Stack>
                      {s.status !== "GREEN" && (
                        <Button size="small" startIcon={<InfoOutlined />} onClick={() => explain(s.ruleId)}>
                          Explain
                        </Button>
                      )}
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: "52px" }}>{s.reason}</Typography>
                    {coach[s.ruleId] && (
                      <Alert severity="info" icon={<InfoOutlined />} sx={{ ml: "52px", mt: 0.75 }}>
                        <Typography variant="body2">{coach[s.ruleId].suggestion}</Typography>
                        {coach[s.ruleId].standardRef && (
                          <Typography variant="caption" color="text.secondary">Ref: {coach[s.ruleId].standardRef}</Typography>
                        )}
                        {coach[s.ruleId].disclaimer && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5, fontStyle: "italic" }}>
                            {coach[s.ruleId].disclaimer}
                          </Typography>
                        )}
                      </Alert>
                    )}
                  </Box>
                ))}
              </Card>
            </Collapse>
          ))}
        </>
      )}

      {!data && !loading && (
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography color="text.secondary">Select a job to see its readiness.</Typography>
        </Card>
      )}
    </Container>
  );
}
